import axios from 'axios';

// Base configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on the login page, affiliates page, or if it's a login request
      const isLoginPage = window.location.pathname === '/login';
      const isAffiliatesPage = window.location.pathname.includes('/affiliate');
      const isLoginRequest = error.config?.url?.includes('/auth/login');

      if (!isLoginPage && !isAffiliatesPage && !isLoginRequest) {
        // Clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh'),
  requestPasswordReset: (email) => api.post('/auth/forgot-password', email),
  verifyResetToken: (token) => api.post('/auth/verify-reset-token', token),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// OAuth API
export const oauthAPI = {
  getInstagramAuthUrl: (accountType = 'business', purpose = 'backup') => api.get('/oauth/instagram/login', { params: { account_type: accountType, purpose } }),
  getFacebookAuthUrl: () => api.get('/oauth/facebook/auth-url'),
  getGoogleAuthUrl: () => api.get('/oauth/google/auth-url'),
  upgradeInstagramPermissions: (accountId) => api.get(`/oauth/instagram/upgrade-permissions/${accountId}`),
  handleInstagramCallback: (code, state) => api.post('/oauth/instagram/callback', { code, state }),
  handleFacebookCallback: (code, state) => api.post('/oauth/facebook/callback', { code, state }),
  handleGoogleCallback: (code, state) => api.post('/oauth/google/callback', { code, state }),
  getStatus: () => api.get('/oauth/status'),
  disconnectInstagram: () => api.delete('/oauth/instagram/disconnect'),
  disconnectGoogle: () => api.delete('/oauth/google/disconnect'),
  
  // Connection monitoring endpoints
  getConnectionHealth: () => api.get('/oauth/connection-health'),
  refreshConnections: () => api.post('/oauth/refresh-connections'),
  getConnectionStatus: () => api.get('/oauth/connection-status'),
  
  // Analytics/logging endpoints
  logPersonalAccountClick: () => api.post('/oauth/instagram/log-personal-account-click'),

  // Instagram survey endpoints
  shouldShowSurvey: () => api.get('/oauth/instagram/should-show-survey'),
  submitSurveyResponse: (reason, comment) => api.post('/oauth/instagram/survey-response', { reason, comment }),
};

// Sync API
export const syncAPI = {
  startSync: (accountId = null) => api.post('/sync/start', accountId ? { account_id: accountId } : {}),
  getSyncJobs: (limit = 10) => api.get(`/sync/sync-jobs?limit=${limit}`),
  getSyncJob: (jobId) => api.get(`/sync/sync-jobs/${jobId}`),
  cancelSyncJob: (jobId) => api.delete(`/sync/sync-jobs/${jobId}`),
  getPosts: (params = {}) => {
    // Filter out undefined, null, and empty string values
    const filteredParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    
    const query = new URLSearchParams(filteredParams).toString();
    return api.get(`/sync/posts?${query}`);
  },
  getPostsPreview: (params = {}) => {
    // Filter out undefined, null, and empty string values
    const filteredParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    
    const query = new URLSearchParams(filteredParams).toString();
    return api.get(`/sync/posts/preview?${query}`);
  },
  getBackups: (params = {}) => {
    // Filter out undefined, null, and empty string values
    const filteredParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    
    const query = new URLSearchParams(filteredParams).toString();
    return api.get(`/sync/backups?${query}`);
  },
  getDashboardStats: () => api.get('/sync/dashboard/stats'),
  getSyncStatus: () => api.get('/sync/status'),
  startInstagramSync: (accountId = null) => api.post('/sync/start', accountId ? { account_id: accountId } : {}),
  retryBackup: (postId) => api.post(`/sync/backups/${postId}/retry`),
  // Organization endpoints
  organizeBackups: () => api.post('/sync/organize-backups'),
  getOrganizationInfo: () => api.get('/sync/organization-info'),
  
  // Enhanced sync endpoints
  getSetupStatus: () => api.get('/sync/setup-status'),
  completeInitialSetup: (data) => api.post('/sync/initial-setup', data),
  startSyncWithRange: (data) => api.post('/sync/start-with-range', data),
  getHealthStatus: () => api.get('/sync/health-status'),
  
  // Check for new posts (mainly for free users)
  checkNewPosts: () => api.get('/sync/check-new-posts'),
  
  // Refresh Instagram posts immediately
  refreshInstagramPosts: (force = false) => api.post('/sync/refresh-instagram-posts', { force }),
  
  // Get the main backup folder URL in Google Drive
  getBackupFolderUrl: () => api.get('/sync/backup-folder-url'),
};

// Accounts API
export const accountsAPI = {
  getInstagramAccounts: () => api.get('/accounts/instagram-accounts'),
  disconnectInstagramAccount: (accountId) => api.delete(`/accounts/instagram-accounts/${accountId}`),
  refreshInstagramProfile: (accountId) => api.put(`/accounts/instagram-accounts/${accountId}/refresh-profile`),
};

// Payment API
export const paymentAPI = {
  // Get payment configuration (provider, product IDs, etc.)
  getConfig: () => api.get('/payment/config'),
};

// Subscription API
export const subscriptionAPI = {
  // Get subscription plans
  getPlans: () => api.get('/subscriptions/plans'),
  
  // Get current subscription
  getCurrentSubscription: () => api.get('/subscriptions/current'),
  
  // Get features for a tier
  getFeatures: (tier) => api.get(`/subscriptions/features/${tier}`),
  
  // Create checkout session
  createCheckoutSession: (data) => api.post('/subscriptions/checkout', data),
  
  // Create customer portal session
  createPortalSession: () => api.post('/subscriptions/portal'),
  
  // Cancel subscription
  cancelSubscription: () => api.post('/subscriptions/cancel'),
  
  // Check subscription status (for post-checkout update)
  checkStatus: () => api.post('/subscriptions/check-status'),
  
  // Reactivate subscription
  reactivateSubscription: () => api.post('/subscriptions/reactivate'),
  
  // Sync development subscription
  syncDevSubscription: () => api.post('/subscriptions/sync-dev'),
};

// Referral API
export const referralAPI = {
  // Get user's referral link
  getMyReferralLink: () => api.get('/referrals/my-link'),
  
  // Get user's referral statistics
  getMyReferralStats: () => api.get('/referrals/my-stats'),
  
  // Get recent referrals
  getRecentReferrals: (limit = 10) => api.get(`/referrals/recent?limit=${limit}`),
  
  // Track referral visit (public, no auth)
  trackVisit: (code) => api.post(`/referrals/track-visit?code=${code}`),
  
  // Validate referral code (public, no auth)
  validateCode: (code) => api.get(`/referrals/validate/${code}`),
};

// Affiliate API
export const affiliateAPI = {
  // Validate affiliate code (public, no auth)
  validateCode: (code) => api.get(`/aff/${code}/validate`),
  
  // Track affiliate visit (public, no auth)
  trackVisit: (code, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.post(`/aff/${code}/visit${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get affiliate link info
  getLink: (code, includePlaceholders = true) => 
    api.get(`/aff/${code}/link?include_placeholders=${includePlaceholders}`),
};

// Chat API (AI Support)
export const chatAPI = {
  // Send a message to the AI chatbot
  sendMessage: (message, conversationHistory = []) => 
    api.post('/chat/message', { message, conversation_history: conversationHistory }),
  
  // Check if chat service is available
  getHealth: () => api.get('/chat/health'),
};

// Content Restore API
export const restoreAPI = {
  // Get posts eligible for restore (have completed backups)
  getEligiblePosts: (params = {}) => {
    const filteredParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(filteredParams).toString();
    return api.get(`/restore/eligible-posts?${query}`);
  },
  
  // Get post count by date range
  getPostsCountByDateRange: (startDate, endDate) => 
    api.get(`/restore/eligible-posts/by-date-range?start_date=${startDate}&end_date=${endDate}`),
  
  // Start a restore job
  startRestore: (data) => api.post('/restore/start', data),
  
  // Get all restore jobs
  getRestoreJobs: (limit = 10) => api.get(`/restore/jobs?limit=${limit}`),
  
  // Get a specific restore job
  getRestoreJob: (jobId, includeItems = false) => 
    api.get(`/restore/jobs/${jobId}?include_items=${includeItems}`),
  
  // Cancel a restore job
  cancelRestoreJob: (jobId) => api.delete(`/restore/jobs/${jobId}`),
  
  // Pause a restore job
  pauseRestoreJob: (jobId) => api.put(`/restore/jobs/${jobId}/pause`),
  
  // Resume a restore job
  resumeRestoreJob: (jobId) => api.put(`/restore/jobs/${jobId}/resume`),
  
  // Get items for a restore job
  getRestoreItems: (jobId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/restore/jobs/${jobId}/items?${query}`);
  },
};

// Utility functions
export const setAuthToken = (token) => {
  if (token) {
    // Store in both localStorage and sessionStorage for iOS Safari compatibility
    localStorage.setItem('token', token);
    sessionStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('oauth_token');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('oauth_token');
    delete api.defaults.headers.common['Authorization'];
  }
};

export const getAuthToken = () => {
  // Check localStorage first, then sessionStorage (iOS Safari compatibility)
  // sessionStorage is more reliable on iOS during OAuth redirects
  return localStorage.getItem('token') || 
         sessionStorage.getItem('token') || 
         localStorage.getItem('oauth_token') || 
         sessionStorage.getItem('oauth_token');
};

export const isAuthenticated = () => {
  return !!getAuthToken();
};

export default api; 

